import styled from "styled-components";

export const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

export const colors = {
  primary: "#F4A01C",
  primaryHover: "#DC901A",
  secondary: "#000000",
  secondaryHover: "#2D2D2D",
  text: "#000000",
  textHover: "#2D2D2D",
  error: "#FF0000",
  background: "#FFFFFF",
  border: "#E0E0E0",
  success: "#4CAF50",
};

// Styled Components

export const NoOverFlowContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const FormContainer = styled.div<{
  $isProfile?: boolean;
}>`
  padding: ${({ $isProfile, theme }) => ($isProfile ? 0 : theme.spacing.xxl)};

  width: 100%;
  margin: 0 auto;
  overflow-x: hidden;

  h2 {
    margin-bottom: 32px;
  }

  @media (max-width: 767px) {
    padding: ${({ $isProfile, theme }) => ($isProfile ? 0 : theme.spacing.md)};
    width: 100%;
  }

  @media (max-width: 375px) {
    padding: ${({ $isProfile, theme }) => ($isProfile ? 0 : theme.spacing.sm)};
    width: 100%;
  }
`;

export const FullWidthContainer = styled.div`
  width: 100%;
`;

export const FlexContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

export const FlexContainerResponsive = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 1280px) {
    flex-direction: row;

    gap: ${({ theme }) => theme.spacing.md};
    justify-content: space-between;
  }
`;

export const ShopNameContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-right: 4px;
`;

export const LinkContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 678px) {
    width: 100%;
    height: auto;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const FormTitle = styled.div`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.xlarge};
  font-weight: ${({ theme }) => theme.fontWeights.normal};
`;

export const FormSubTitle = styled.div<{
  $hasError?: boolean;
  $marginTop?: string;
  $marginBottom?: string;
}>`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme, $marginBottom }) =>
    $marginBottom || theme.spacing.sm};
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  margin-top: ${({ $hasError, $marginTop }) => $marginTop || "0px"};
`;

export const DeactivateTitle = styled.div<{
  $hasError?: boolean;
  $marginTop?: string;
  $mb?: string;
}>`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme, $mb }) => $mb || theme.spacing.sm};
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin-top: ${({ $hasError, $marginTop }) => $marginTop || "0px"};
`;

export const FormGroup = styled.div<{
  $hasError?: boolean;
  $marginBottom?: string;
  $paddingRight?: string;
}>`
  background: rgba(132, 73, 2, 0.08);
  height: 74px;
  border-radius: 4px;
  padding-right: ${({ $paddingRight }) => $paddingRight || "16px"};
  padding-left: 16px;
  padding-top: 16px;
  padding-bottom: 16px;

  margin-top: ${({ $marginBottom }) => $marginBottom || "16px"};
  display: flex;
  position: relative;
  width: 100%;

  @media (max-width: 767px) {
    max-width: 100%;
    padding: 12px 8px 12px 8px;
  }
`;

export const Label = styled.label`
  display: block;
  font-weight: ${({ theme }) => theme.fontWeights.xbold};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.small};
`;

export const InputField = styled.div<{ $hasError?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: inherit;
  border: none;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.xbold};
  margin-top: -8px;
  width: 100%;
`;

export const InputIcon = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  margin-right: ${({ theme }) => theme.spacing.md};

  @media (max-width: 767px) {
    margin-right: ${({ theme }) => theme.spacing.sm};
    width: 24px;
  }
`;

export const DropdownArrowIcon = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;

  @media (max-width: 767px) {
    margin-right: ${({ theme }) => theme.spacing.sm};
    width: 24px;
  }
`;

export const Input = styled.input<{
  $hasError?: boolean;
  width?: string;
  color?: string;
}>`
  width: ${({ width }) => width ?? "calc(100% - 24px)"};
  padding: 12px 16px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: ${({ theme, color }) => color || theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeights.xbold};

  &::placeholder {
    color: ${({ theme }) => theme.colors.placeholder};
  }

  &:focus {
    background: transparent;
  }
`;

export const Select = styled.select<{ $hasError?: boolean }>`
  width: calc(100% - 24px);
  padding: 12px 36px 12px 16px;

  border: none;
  appearance: none;
  background: inherit;
  font-size: 14px;
  cursor: pointer;
  outline: none;
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeights.xbold};

  &::placeholder {
    color: ${({ theme }) => theme.colors.placeholder};
  }

  &:focus {
    background: transparent;
  }
`;

export const ErrorText = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "marginBottom",
})<{ marginBottom?: string }>`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  margin-bottom: ${({ marginBottom }) => marginBottom ?? "8px"} !important;
  display: block;
  line-height: 1.4;
  margin-top: 4px;
`;

export const ButtonContainer = styled.div<{
  $mt?: string;
  $pt?: string;
}>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-top: ${({ $pt }) => ($pt ? $pt : "0px")};
  margin-top: ${({ $mt, theme }) => ($mt ? $mt : theme.spacing.xl)};
`;

export const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
`;

export const BackButton = styled(Button)`
  background-color: #a5a5a5 !important;
  color: #fff !important;
  margin-bottom: 8px;
  width: 60px !important;
  height: 56px !important;
  border: none !important;
  font-size: 48px;
`;

export const CancelButton = styled(Button)<{
  $width?: string;
  $height?: string;
}>`
  background-color: #a5a5a5 !important;
  color: #fff !important;
  margin-bottom: 8px;
  width: ${({ $width }) => $width || "155px"};
  height: ${({ $height }) => $height || "56px"};
  border: none !important;
  font-size: 16px;
`;

export const SkipButton = styled(Button)`
  background-color: #a5a5a5 !important;
  color: #fff !important;
  margin-bottom: 8px;
  width: 60px !important;
  height: 56px !important;
  border: none !important;
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: ${({ theme }) => theme.fontWeights.xbold};
  min-width: 100px !important;
`;

export const NextButton = styled(Button)<{
  $width?: string;
  $height?: string;
  $bgColor?: string;
}>`
  background-color: ${({ $bgColor }) => $bgColor || "#e39435 !important"};
  color: #fff !important;
  margin-bottom: 8px;
  width: ${({ $width }) => $width || "155px"};
  height: ${({ $height }) => $height || "56px"};
  border: none !important;
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: ${({ theme }) => theme.fontWeights.xbold};
`;

export const PasswordToggle = styled.button<{
  $hasError?: boolean;
  marginTop?: string;
}>`
  position: absolute;
  right: 6px;
  background: none;
  border: none;
  cursor: pointer;
  color: #9f9f9f;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 0;
  margin-top: ${({ $hasError, marginTop }) => marginTop || "0px"};
  background: transparent;
  border: none;
`;

export const PhonePrefix = styled.span`
  position: absolute;
  pointer-events: none;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.xbold};
  margin-top: -1px;
  color: ${({ color }) => color};
`;

export const NameFieldsContainer = styled.div`
  display: flex;
  width: 100%;
`;

export const NameFieldWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StyledPersonIcon = styled.div`
  margin-top: 10px;
  margin-right: 16px;
  margin-left: 4px;
  color: #e39435;
  display: flex;
  align-items: center;
`;
